import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon } from '@persona-id/design-tokens/icons';
import { useRouteMatch } from 'react-router-dom';
import { Link, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { REVIEW_CHANGES_PATH } from 'dashboard/apps/VerificationTemplateEditor/VerificationTemplateEditor';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned';
import Banner from 'dashboard/components-lib/Banner';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStatus,
} from 'dashboard/models/inquiry-template-version';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import {
  VerificationTemplateVersion,
  VerificationTemplateVersionStatus,
} from 'dashboard/models/verification-template-version';
import { getEditorPath as getInquiryEditorPath } from 'dashboard/redux/thunk-actions/inquiry-template';
import { getEditorPath as getVerificationEditorPath } from 'dashboard/redux/thunk-actions/verification-template';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { Variant } from 'design-system/theme';
import useConfirmation from 'lib/hooks/useConfirmation';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import { Permission } from 'lib/permissions';
import { assertNever } from 'lib/utils/types';

const StyledNavBack = styled(NavBack)`
  padding-left: ${Spacing[2]};
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: -${Spacing[2]};
  margin-right: ${Spacing[4]};
  overflow: hidden;
`;

const StickerWrapper = styled.div`
  display: flex;
  margin-right: ${Spacing[2]};
  margin-top: auto;
  margin-bottom: auto;
`;

const HeaderButton = styled(Button).attrs(() => ({ width: 'auto' }))``;

export interface PendingStates {
  isDiscarding: boolean;
  isLoading: boolean;
  isPublishing: boolean;
  isUpdating: boolean;
}

interface VerificationTemplateHeaderProps {
  verificationTemplate: VerificationTemplate;
  verificationTemplateVersion: VerificationTemplateVersion;
  relatedInquiryTemplateVersions: Partial<InquiryTemplateVersion>[];
  pendingStates: PendingStates;
  isFormPristine: boolean;
  isFormValid: boolean;
  displayEditingActions: boolean;
  showJsonDiff: boolean;
  handleDiscardDraft: (verificationTemplateId: string) => void;
  handlePublishDraft: () => void;
  handleSave: () => void;
  setJsonDiff: (value: boolean) => void;
}

const VerificationTemplateHeader = ({
  verificationTemplate,
  verificationTemplateVersion,
  relatedInquiryTemplateVersions,
  pendingStates,
  isFormPristine,
  isFormValid,
  displayEditingActions,
  showJsonDiff,
  handleDiscardDraft,
  handlePublishDraft,
  handleSave,
  setJsonDiff,
}: VerificationTemplateHeaderProps) => {
  const { isDiscarding, isPublishing, isUpdating } = pendingStates;

  const isPersonerd = useIsPersonerd();
  const navigate = useNavigate();
  const matchReviewChangesPath = useRouteMatch({
    path: REVIEW_CHANGES_PATH,
    strict: false,
  });
  const showPublishDiff =
    !matchReviewChangesPath &&
    verificationTemplate.relationships['latest-published-version'] != null;
  const [
    showDiscardDraftModal,
    { prompt: promptDiscard, confirm: confirmDiscardDraft, cancel: cancelDiscardDraft },
  ] = useConfirmation();
  const [
    showPublishDraftModal,
    { prompt: promptPublish, confirm: confirmPublishDraft, cancel: cancelPublishDraft },
  ] = useConfirmation();
  const [
    showSaveDraftModal,
    { prompt: promptSave, confirm: confirmSaveDraft, cancel: cancelSaveDraft },
  ] = useConfirmation();
  const {
    id: verificationTemplateId,
    attributes: { name: verificationTemplateName },
    relationships: {
      'latest-draft-version': latestDraftVersion,
      'latest-published-version': latestPublishedVersion,
    },
  } = verificationTemplate;
  const {
    id: versionId,
    attributes: { status: versionStatus },
  } = verificationTemplateVersion;

  // disable header buttons whenever VTE is in any pending state
  const processing = Object.values(pendingStates).some((pendingState) => !!pendingState);

  const BASE_PATH = getVerificationEditorPath(
    verificationTemplate.id,
    verificationTemplateVersion.id,
  );
  const handlePublish = () => {
    if (showPublishDiff) navigate(BASE_PATH + '/review-changes');
    else promptPublish(() => handlePublishDraft());
  };

  const onBackClick = () => navigate(-1);

  return (
    <>
      <ModalWrapper isOpen={showDiscardDraftModal} handleCloseModal={cancelDiscardDraft}>
        <ConfirmationModal
          cancelAction={cancelDiscardDraft}
          confirmAction={confirmDiscardDraft}
          header='Discard draft?'
          confirmActionText='Discard'
        >
          All unpublished changes in this draft will be discarded. This action is permanent and
          cannot be undone.
        </ConfirmationModal>
      </ModalWrapper>

      <ModalWrapper isOpen={showSaveDraftModal} handleCloseModal={cancelSaveDraft}>
        <ConfirmationModal
          cancelAction={cancelSaveDraft}
          confirmAction={confirmSaveDraft}
          header={
            versionStatus === VerificationTemplateVersionStatus.Draft
              ? 'Update existing draft?'
              : 'Create new draft?'
          }
          confirmActionText='Save'
        >
          Your changes won&apos;t be live until you publish the template
        </ConfirmationModal>
      </ModalWrapper>

      <ModalWrapper isOpen={showPublishDraftModal} handleCloseModal={cancelPublishDraft}>
        <ConfirmationModal
          cancelAction={cancelPublishDraft}
          confirmAction={confirmPublishDraft}
          header='Publish draft?'
          confirmActionText='Publish'
        >
          Publishing will affect any future production verifications made with this template.
        </ConfirmationModal>
      </ModalWrapper>

      <div>
        {versionId != null && (
          <StatusBanner
            verificationTemplateId={verificationTemplateId}
            verificationTemplateVersionId={versionId}
            verificationTemplateName={verificationTemplateName}
            relatedInquiryTemplateVersions={relatedInquiryTemplateVersions}
            latestPublishedVersionId={latestPublishedVersion.data?.id}
            latestDraftVersionId={latestDraftVersion.data?.id}
            status={versionStatus}
          />
        )}

        <FullScreenHeaderContainer>
          <HorizontalContainer>
            <StyledNavBack to='/dashboard/verification-templates'>
              {verificationTemplateName}
            </StyledNavBack>
            <StickerWrapper>
              <StatusSticker status={versionStatus} />
            </StickerWrapper>
          </HorizontalContainer>

          <HStack spacing={Spacing[3]}>
            <Permissioned permission={Permission.VerificationTemplateWrite} fallback={null}>
              {displayEditingActions && (
                <>
                  {matchReviewChangesPath && isPersonerd && (
                    <ToggleSlider
                      labelOn='View as JSON'
                      labelOff='View as JSON'
                      value={showJsonDiff}
                      onChange={(value) => setJsonDiff(value)}
                    />
                  )}
                  {matchReviewChangesPath && (
                    <HeaderButton
                      id='templates-view__return-to-draft-button'
                      onClick={onBackClick}
                      variant='basic'
                    >
                      Back to Draft
                    </HeaderButton>
                  )}
                  {versionStatus === VerificationTemplateVersionStatus.Draft && (
                    <HeaderButton
                      id='templates-view__publish-draft'
                      aria-label='Publish'
                      disabled={processing || !isFormPristine}
                      isPending={isPublishing}
                      onClick={handlePublish}
                    >
                      Publish
                    </HeaderButton>
                  )}
                  {!matchReviewChangesPath && (
                    <HeaderButton
                      id='templates-view__save-template'
                      aria-label='Save'
                      disabled={processing || isFormPristine || !isFormValid}
                      isPending={isUpdating}
                      onClick={() => promptSave(() => handleSave())}
                    >
                      Save
                    </HeaderButton>
                  )}
                  {versionStatus === VerificationTemplateVersionStatus.Draft &&
                    latestPublishedVersion.data?.id != null &&
                    !matchReviewChangesPath && (
                      <HeaderButton
                        id='templates-view__discard-draft'
                        variant='basic'
                        aria-label='Discard'
                        disabled={processing}
                        isPending={isDiscarding}
                        onClick={() =>
                          promptDiscard(() => handleDiscardDraft(verificationTemplateId))
                        }
                      >
                        Discard
                      </HeaderButton>
                    )}
                </>
              )}
            </Permissioned>

            <FeatureFlagged featureFlag='global:enable-platform-connection-dashboard'>
              <DropdownMenu
                trigger={
                  <Button aria-label='More actions' type='button' variant='inverse' width='icon'>
                    <MoreIcon />
                  </Button>
                }
              >
                <DropdownMenu.Item as={Link} to={`/dashboard/platform-connections/${versionId}`}>
                  Connections
                </DropdownMenu.Item>
              </DropdownMenu>
            </FeatureFlagged>
          </HStack>
        </FullScreenHeaderContainer>
      </div>
    </>
  );
};

export interface StatusBannerProps {
  verificationTemplateId: string;
  verificationTemplateVersionId: string;
  verificationTemplateName: string;
  relatedInquiryTemplateVersions: Partial<InquiryTemplateVersion>[];
  latestPublishedVersionId?: string;
  latestDraftVersionId?: string;
  status: VerificationTemplateVersionStatus;
}

export const StatusBanner = ({
  verificationTemplateId,
  verificationTemplateVersionId,
  verificationTemplateName,
  relatedInquiryTemplateVersions,
  latestPublishedVersionId,
  latestDraftVersionId,
  status,
}: StatusBannerProps) => {
  const filteredRelatedInquiryTemplateVersions = relatedInquiryTemplateVersions?.filter(
    (inquiryTemplateVersion) =>
      !relatedInquiryTemplateVersions.some(
        (itv) =>
          // filter out the current version and any versions that are not drafts or are older than the current version
          // when there are multiple versions from the same inquiry template
          inquiryTemplateVersion.id !== itv.id &&
          inquiryTemplateVersion.relationships?.['inquiry-template']?.data?.id ===
            itv.relationships?.['inquiry-template']?.data?.id &&
          ((itv.attributes?.status === InquiryTemplateVersionStatus.Draft &&
            inquiryTemplateVersion.attributes?.status === InquiryTemplateVersionStatus.Published) ||
            (itv.attributes?.['updated-at'] &&
              inquiryTemplateVersion.attributes?.['updated-at'] &&
              itv.attributes['updated-at'] > inquiryTemplateVersion.attributes['updated-at'])),
      ),
  );
  if (filteredRelatedInquiryTemplateVersions?.length > 0) {
    return (
      <Banner>
        This Verification Template Version is editable through the Flow Editor -{' '}
        <>
          {filteredRelatedInquiryTemplateVersions.map((inquiryTemplateVersion, index) => {
            if (
              inquiryTemplateVersion.id &&
              inquiryTemplateVersion.relationships?.['inquiry-template']?.data?.id
            )
              return (
                <>
                  <Link
                    to={getInquiryEditorPath(
                      inquiryTemplateVersion.relationships['inquiry-template'].data.id,
                      inquiryTemplateVersion.id,
                      `config/verifications/${verificationTemplateVersionId}`,
                    )}
                  >
                    {filteredRelatedInquiryTemplateVersions.length === 1
                      ? 'view the inquiry template.'
                      : inquiryTemplateVersion.id}
                  </Link>
                  {index !== filteredRelatedInquiryTemplateVersions.length - 1 && ', '}
                </>
              );
          })}
        </>
      </Banner>
    );
  } else if (status === VerificationTemplateVersionStatus.Published) {
    if (verificationTemplateVersionId === latestPublishedVersionId) {
      return latestDraftVersionId ? (
        <Banner>
          A draft exists for <strong>{verificationTemplateName}</strong>.
          <>
            {' This version is no longer editable - '}
            <Link to={getVerificationEditorPath(verificationTemplateId, latestDraftVersionId)}>
              view the current draft version.
            </Link>
          </>
        </Banner>
      ) : null;
    } else {
      const activeVersionId =
        latestDraftVersionId != null ? latestDraftVersionId : latestPublishedVersionId;
      const buttonText =
        latestDraftVersionId != null ? 'view the current draft.' : 'view the active version.';
      if (activeVersionId)
        return (
          <Banner>
            You are viewing a previously published version for{' '}
            <strong>{verificationTemplateName}</strong>.
            <>
              {' This version is no longer editable - '}
              <Link to={getVerificationEditorPath(verificationTemplateId, activeVersionId)}>
                {buttonText}
              </Link>
            </>
          </Banner>
        );
    }
  }
  return <></>;
};

const StatusSticker = ({ status }: { status: VerificationTemplateVersionStatus }) => {
  const getStickerStatus = (): [Exclude<Variant, 'inverse'>, string] => {
    switch (status) {
      case VerificationTemplateVersionStatus.Published: {
        return ['success', 'Published'];
      }
      case VerificationTemplateVersionStatus.Draft: {
        return ['primary', 'Draft'];
      }
      default:
        assertNever(status);
    }
  };
  const [variantType, statusMessage] = getStickerStatus();
  return <Sticker variant={variantType}>{statusMessage}</Sticker>;
};

export default VerificationTemplateHeader;
